import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { inputStyle, buttonStyleGreen } from '../styles';
import { DISCOVERY_INTERESTS, normalizeInterestIds } from '../services/discoveryConfig';

function Signup({ onSwitchToLogin }) {
  const [username, setUsername] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [interestIds, setInterestIds] = useState([]);
  const [loading, setLoading]   = useState(false);

  const signUp = async () => {
    if (!username || !email || !password) {
      alert('Please fill in all fields');
      return;
    }
    if (interestIds.length === 0) {
      alert('Please choose at least one Discovery interest');
      return;
    }
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        username,
        tokens: 0,
        interestIds: normalizeInterestIds(interestIds),
        interestVersion: 1,
        onboardingCompletedAt: new Date().toISOString(),
        completedLinks: [],
        joinDate: new Date().toISOString(),
        totalEarned: 0,
        lastActive: new Date().toISOString(),
      }, { merge: true });
      // Auth state change will handle navigation
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interestId) => {
    setInterestIds((currentIds) => {
      const current = normalizeInterestIds(currentIds);
      return current.includes(interestId)
        ? current.filter((id) => id !== interestId)
        : [...current, interestId];
    });
  };

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {/* Left: hero image */}
      <div
        style={{
          flex: 1,
          backgroundImage:
            'url(https://promogrindmaterials.s3.us-west-1.amazonaws.com/Promogrind8437493.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Right: form + how-it-works */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 40,
          overflowY: 'auto',
        }}
      >
        {/* Signup card */}
        <div
          style={{
            backgroundColor: 'white',
            padding: 40,
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            width: 400,
            textAlign: 'center',
          }}
        >
          <img
            src="https://promogrindmaterials.s3.us-west-1.amazonaws.com/Promogrindlogo93494.jpg"
            alt="PromoGrind Logo"
            style={{ width: 120, marginBottom: 20 }}
          />
          <h1 style={{ marginBottom: 20, color: '#333' }}>Create Your PromoGrind Account</h1>
          <p style={{ color: '#666', marginBottom: 20 }}>
            Join creators discovering content and sharing useful feedback
          </p>

          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={inputStyle}
            disabled={loading}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            disabled={loading}
            onKeyPress={(e) => e.key === 'Enter' && signUp()}
          />

          <fieldset style={interestFieldsetStyle}>
            <legend style={interestLegendStyle}>Discovery Interests</legend>
            <p style={interestHelperStyle}>Choose what you want to discover. This is not your profession.</p>
            <div style={interestGridStyle}>
              {DISCOVERY_INTERESTS.map((interest) => {
                const checked = interestIds.includes(interest.id);
                return (
                  <label key={interest.id} style={interestOptionStyle(checked)}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={loading}
                      onChange={() => toggleInterest(interest.id)}
                    />
                    <span>{interest.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <button
            onClick={signUp}
            style={{
              ...buttonStyleGreen,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
          >
            {loading ? '🔄 Creating Account...' : 'Sign Up'}
          </button>

          <p style={{ marginTop: 20 }}>
            Already have an account?{' '}
            <span
              style={{
                color: '#673AB7',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={onSwitchToLogin}
            >
              Sign in here
            </span>
          </p>
        </div>

        {/* How It Works section (INSIDE the right column, BELOW the card) */}
        <div
          className="how-it-works"
          style={{
            width: 400,
            maxWidth: '90vw',
            marginTop: 24,
            color: '#333',
            textAlign: 'left',
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            padding: 24,
          }}
        >
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>How It Works</h2>

          <div style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 6px 0', fontSize: 16 }}>Create a Discovery Campaign</h3>
            <p style={{ margin: 0, color: '#555' }}>
              Share your music, videos, art, or ideas as internal PromoGrind discovery cards.
              Optional creator links can provide context without creating external reward requirements.
            </p>
          </div>

          <div style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 6px 0', fontSize: 16 }}>Discover & Give Feedback</h3>
            <p style={{ margin: 0, color: '#555' }}>
              Explore creator campaigns inside PromoGrind and help creators understand what is clear,
              interesting, or useful about their work.
            </p>
          </div>

          <div>
            <h3 style={{ margin: '0 0 6px 0', fontSize: 16 }}>Grow Through Discovery</h3>
            <p style={{ margin: 0, color: '#555' }}>
              Use your token balance to create campaigns that invite internal discovery and feedback.
              External visits and engagement never earn PromoGrind tokens.
            </p>
          </div>

          <div
            style={{
              marginTop: 16,
              fontSize: 12,
              color: '#777',
              borderTop: '1px solid #eee',
              paddingTop: 12,
              textAlign: 'center',
            }}
          >
            <strong>SouthernMention</strong>
            <br />
            © 2025 Promogrind. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

const interestFieldsetStyle = {
  border: '1px solid #d8dee6',
  borderRadius: 8,
  margin: '8px 0 16px',
  padding: 14,
  textAlign: 'left'
};

const interestLegendStyle = {
  color: '#333',
  fontWeight: 'bold',
  padding: '0 6px'
};

const interestHelperStyle = {
  color: '#666',
  fontSize: 12,
  margin: '0 0 10px'
};

const interestGridStyle = {
  display: 'grid',
  gap: 8,
  gridTemplateColumns: '1fr'
};

const interestOptionStyle = (checked) => ({
  alignItems: 'center',
  backgroundColor: checked ? '#eef7ff' : '#fff',
  border: `1px solid ${checked ? '#007bff' : '#d8dee6'}`,
  borderRadius: 8,
  color: '#333',
  display: 'flex',
  fontSize: 13,
  fontWeight: checked ? 'bold' : 'normal',
  gap: 8,
  padding: '8px 10px'
});

export default Signup;
